import React from "react";
import { Package, AlertTriangle, CheckCircle, Boxes } from "lucide-react";
import { ActiveAntsPurchaseOrder, Product } from "src/types";
import {
  ResolveAAErrorFormData,
  ResolveAAErrorFormDataKey,
  ResolveAAErrorFormAction,
  ProductEuMappingUpdate
} from "src/types/forms/activeAntsPurchaseOrders/resolveAAErrorForm";
import { ValidationErrors } from "src/errors";
import { useScrollOnMessage } from "src/hooks";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { TextInputBox } from "src/components/ui/inputs";
import { Table } from "src/components";
import { ProductColumnHeader } from "src/enums";
import { TableColumn } from "src/types";

interface ResolveAAErrorFormProps {
  formData: ResolveAAErrorFormData;
  activeAntsPurchaseOrder?: ActiveAntsPurchaseOrder;
  productEuUpdates: ProductEuMappingUpdate[];
  validationErrors?: ValidationErrors<ResolveAAErrorFormDataKey>;
  formLvlErrorMessage: string;
  successMessage: string;
  isFormComplete: boolean;
  handleInputChange: (field: ResolveAAErrorFormDataKey, value: string) => void;
  handleEuFieldChange: (productId: string, field: "euSku" | "euBarcode" | "euVariantId", value: string) => void;
  handleSubmit: (e?: React.FormEvent) => void;
  setAction: (action: ResolveAAErrorFormAction) => void;
  isSubmitting: boolean;
}

const ResolveAAErrorForm: React.FC<ResolveAAErrorFormProps> = ({
  formData,
  activeAntsPurchaseOrder,
  productEuUpdates,
  validationErrors,
  formLvlErrorMessage,
  successMessage,
  isFormComplete,
  handleInputChange,
  handleEuFieldChange,
  handleSubmit,
  setAction,
  isSubmitting
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const getUpdate = (productId: string) => productEuUpdates.find((u) => u.productId === productId);

  const productTableColumns: TableColumn<ProductColumnHeader, Product>[] = [
    {
      header: ProductColumnHeader.ID,
      column: (row: Product) => <span className="font-medium text-neutral-900">{row.id}</span>,
      styles: { minWidth: "140px", width: "140px" }
    },
    {
      header: ProductColumnHeader.NAME,
      column: (row: Product) => <span className="text-neutral-900">{row.name}</span>,
      styles: { minWidth: "220px" }
    },
    {
      header: "Current EU SKU" as ProductColumnHeader,
      column: (row: Product) => <span className="text-neutral-700">{row.skuName}</span>,
      styles: { minWidth: "160px", width: "160px" }
    },
    {
      header: "Current EU Barcode" as ProductColumnHeader,
      column: (row: Product) => <span className="font-mono text-neutral-900">{row.barcode}</span>,
      styles: { minWidth: "160px", width: "160px" }
    },
    {
      header: "New EU SKU" as ProductColumnHeader,
      column: (row: Product) => (
        <input
          id={`eu-sku-${row.id}`}
          value={getUpdate(row.id)?.euSku ?? ""}
          onChange={(e) => handleEuFieldChange(row.id, "euSku", e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent w-full"
          placeholder="New EU SKU"
        />
      ),
      styles: { minWidth: "180px", width: "180px" }
    },
    {
      header: "New EU Barcode" as ProductColumnHeader,
      column: (row: Product) => (
        <input
          id={`eu-barcode-${row.id}`}
          value={getUpdate(row.id)?.euBarcode ?? ""}
          onChange={(e) => handleEuFieldChange(row.id, "euBarcode", e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent w-full"
          placeholder="New EU Barcode"
        />
      ),
      styles: { minWidth: "180px", width: "180px" }
    },
    {
      header: "New EU Variant ID" as ProductColumnHeader,
      column: (row: Product) => (
        <input
          id={`eu-variant-${row.id}`}
          value={getUpdate(row.id)?.euVariantId ?? ""}
          onChange={(e) => handleEuFieldChange(row.id, "euVariantId", e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent w-full"
          placeholder="New EU Variant ID"
        />
      ),
      styles: { minWidth: "200px", width: "200px" }
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Resolve Active Ants Error</h1>
            <p className="text-gray-300 mt-2 text-lg">
              Edit the SKU / barcode / variant id on the Active Ants mirror products and retry the push to MAYA WMS
            </p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <Package className="w-4 h-4 mr-2" />
                  Active Ants PO Search
                </h3>
                <TextInputBox
                  id="active-ants-purchase-order-id"
                  placeholder="AA-xxxxxxxxx"
                  onChange={(value) => {
                    handleInputChange("activeAntsPurchaseOrderId", value.toUpperCase());
                  }}
                  value={formData.activeAntsPurchaseOrderId}
                />
                <ValidationErrorMessage
                  message={validationErrors?.getErrorMessageByFormDataKey("activeAntsPurchaseOrderId")}
                />
              </div>

              {!activeAntsPurchaseOrder && (
                <button
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                  disabled={!isFormComplete}
                  onClick={() => setAction("FETCH")}
                  type="submit"
                >
                  <Package className="w-4 h-4" />
                  <span>Search</span>
                </button>
              )}

              {activeAntsPurchaseOrder && (
                <>
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <Package className="w-4 h-4 mr-2" />
                      Active Ants PO Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">AA PO Id</p>
                        <p className="text-sm font-medium text-gray-900">{activeAntsPurchaseOrder.id}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
                        <p className="text-sm font-medium text-gray-900">{activeAntsPurchaseOrder.status}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Source</p>
                        <p className="text-sm font-medium text-gray-900">
                          {activeAntsPurchaseOrder.source ?? "SHIPMENT"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {activeAntsPurchaseOrder.aaError && (
                    <div className="border border-error-200 rounded-2xl p-5 bg-error-50">
                      <h3 className="text-sm font-semibold text-error-800 mb-4 tracking-wide uppercase flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Active Ants Error Details
                      </h3>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Error</p>
                          <p className="text-sm font-medium text-error-700">
                            {activeAntsPurchaseOrder.aaError.message.split(":")[0]}
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Timestamp</p>
                          <p className="text-sm font-medium text-gray-900">
                            {new Date(activeAntsPurchaseOrder.aaError.timestamp).toLocaleString()}
                          </p>
                        </div>
                        {activeAntsPurchaseOrder.aaError.details.length > 0 && (
                          <div className="bg-white rounded-lg p-4">
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Details</p>
                            <ul className="list-disc list-inside space-y-1">
                              {activeAntsPurchaseOrder.aaError.details.map((detail, index) => (
                                <li key={index} className="text-sm text-gray-700">
                                  {detail}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <Boxes className="w-4 h-4 mr-2" />
                      Update Active Ants Product Mapping
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Edit the SKU (and optionally barcode / variant id) that Active Ants rejected. Submitting writes
                      the new values onto the AA mirror and retries the push to MAYA WMS.
                    </p>
                    <Table<ProductColumnHeader, Product>
                      data={activeAntsPurchaseOrder.products || []}
                      columns={productTableColumns}
                    />
                  </div>

                  <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

                  {successMessage && (
                    <div className="border border-success-200 rounded-2xl p-5 bg-success-50">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-success-600" />
                        <p className="text-sm font-medium text-success-800">{successMessage}</p>
                      </div>
                    </div>
                  )}

                  <button
                    className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                    disabled={isSubmitting}
                    onClick={() => setAction("SUBMIT")}
                    type="submit"
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>{isSubmitting ? "Updating..." : "Update Mapping & Resend to Active Ants"}</span>
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { ResolveAAErrorForm };
